import type { Answers } from './manifest'

// Template files are stored under their real names (`index.js`, not `index.js.tpl`) so they stay readable
// and syntax-highlighted while being edited. That constrains the substitution syntax: `{{name}}` only ever
// appears inside a string literal, and anything structural is gated by a *line comment* pair, which keeps
// the raw template valid JavaScript.
//
//   // {{#if useFilesApi}}
//   usesFileStorage: true,
//   // {{/if}}
//
// A block may also test a specific value, for answers that are not booleans:
//
//   // {{#if configMode=full}}

const PLACEHOLDER = /\{\{\s*([a-zA-Z0-9_]+)\s*(?:\|\s*([a-zA-Z0-9_]+)\s*)?\}\}/g
const IF_OPEN = /^\s*(?:\/\/|#|<!--)\s*\{\{#if\s+([a-zA-Z0-9_]+)\s*(?:=\s*([a-zA-Z0-9_.-]+)\s*)?\}\}/
const IF_CLOSE = /^\s*(?:\/\/|#|<!--)\s*\{\{\/if\s*\}\}/

const FILTERS: Record<string, (value: string) => string> = {
  titleCase: value => value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' '),
  upperCase: value => value.toUpperCase(),
  lowerCase: value => value.toLowerCase(),
}

export function applyFilter(name: string, value: string): string {
  const filter = FILTERS[name]

  if (!filter) {
    throw new Error(`Unknown template filter "${ name }" — known filters: ${ Object.keys(FILTERS).join(', ') }`)
  }

  return filter(value)
}

/**
 * Substitute `{{name}}` / `{{name|filter}}` against the answers.
 *
 * An unknown key is an error rather than an empty string: a typo in a template would otherwise ship a
 * silently broken service, and the generated file is the thing a user reads to learn the format.
 */
export function interpolate(source: string, answers: Answers, origin: string): string {
  return source.replace(PLACEHOLDER, (_match, name: string, filter?: string) => {
    if (!(name in answers)) {
      throw new Error(`Unresolved placeholder "{{${ name }}}" in ${ origin }`)
    }

    const value = String(answers[name])

    return filter ? applyFilter(filter, value) : value
  })
}

/** Drop the lines inside `{{#if flag}}` blocks whose flag is falsy, and the marker lines themselves. */
export function stripConditionals(source: string, answers: Answers, origin: string): string {
  const lines = source.split('\n')
  const output: string[] = []
  const stack: { name: string, keep: boolean }[] = []

  for (const [index, line] of lines.entries()) {
    const open = IF_OPEN.exec(line)

    if (open) {
      const [, name, expected] = open

      if (!(name in answers)) {
        throw new Error(`Unknown flag "${ name }" in {{#if}} at ${ origin }:${ index + 1 }`)
      }

      // `{{#if name}}` is truthiness; `{{#if name=value}}` compares, for choice answers.
      const matches = expected === undefined
        ? Boolean(answers[name])
        : String(answers[name]) === expected

      // A nested block inside a dropped one stays dropped regardless of its own flag.
      stack.push({ name, keep: matches && stack.every(frame => frame.keep) })

      continue
    }

    if (IF_CLOSE.test(line)) {
      if (!stack.pop()) {
        throw new Error(`Unmatched {{/if}} at ${ origin }:${ index + 1 }`)
      }

      continue
    }

    if (stack.every(frame => frame.keep)) {
      output.push(line)
    }
  }

  if (stack.length > 0) {
    throw new Error(`Unclosed {{#if ${ stack[stack.length - 1].name }}} in ${ origin }`)
  }

  return output.join('\n')
}

/** Full render of one template file: conditionals first, so a dropped block's placeholders never resolve. */
export function render(source: string, answers: Answers, origin: string): string {
  return interpolate(stripConditionals(source, answers, origin), answers, origin)
}
